import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Kanban, User, CalendarBlank, ArrowSquareOut, DotsSixVertical, CurrencyDollar,
} from "@phosphor-icons/react";

const TABS = [
  { key: "deals", label: "Sales Pipeline" },
  { key: "quotes", label: "Quotes" },
  { key: "jobs", label: "Jobs" },
];

const STAGE_ACCENT = {
  new_lead: "#94A3B8", quoted: "#0EA5E9", approved: "#00B4D8", scheduled: "#6366F1",
  in_progress: "#F59E0B", completed: "#22C55E", invoiced: "#8B5CF6", paid: "#16A34A",
  draft: "#94A3B8", sent: "#0EA5E9", rejected: "#EF4444", converted: "#16A34A",
  unscheduled: "#94A3B8", cancelled: "#EF4444",
};

const JOB_STATUS = ["unscheduled", "scheduled", "in_progress", "completed", "cancelled"];
const QUOTE_STATUS = ["draft", "sent", "approved", "rejected", "converted"];
const INVOICE_STATUS = ["draft", "sent", "paid", "overdue"];
const labelize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function Pipeline() {
  const [type, setType] = useState("deals");
  const [board, setBoard] = useState({ stages: [], cards: [] });
  const [loading, setLoading] = useState(true);
  const [crew, setCrew] = useState([]);
  const [drag, setDrag] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.pipelineBoard(type).then(setBoard).catch(() => toast.error("Failed to load pipeline")).finally(() => setLoading(false));
  }, [type]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.listUsers("crew").then(setCrew).catch(() => {}); }, []);

  const crewName = (id) => crew.find((c) => c.id === id)?.name || "Crew";

  const drop = async (stageKey) => {
    const card = drag;
    setDrag(null); setOverCol(null);
    if (!card || card.stage === stageKey) return;
    setBoard((b) => ({ ...b, cards: b.cards.map((c) => c.deal_key === card.deal_key ? { ...c, stage: stageKey } : c) }));
    try {
      const res = await api.pipelineMove({ type, deal_key: card.deal_key, id: card.id, to_stage: stageKey });
      if (res.note) toast.success(res.note);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move card");
      load();
    }
  };

  const cardsFor = (stageKey) => board.cards.filter((c) => c.stage === stageKey);

  return (
    <div className="p-4 sm:p-8">
      <PageHeader title="Pipeline" subtitle="See where every deal stands — drag to move it forward." />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-slate-100 p-1 rounded-lg w-fit">
        {TABS.map((t) => (
          <button
            key={t.key} onClick={() => setType(t.key)} data-testid={`pipeline-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
              type === t.key ? "bg-white text-[#0B1726] shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-20 text-center">Loading pipeline…</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
          {board.stages.map((s) => {
            const items = cardsFor(s.key);
            const accent = STAGE_ACCENT[s.key] || "#94A3B8";
            return (
              <div
                key={s.key}
                onDragOver={(e) => { e.preventDefault(); setOverCol(s.key); }}
                onDragLeave={() => setOverCol((c) => (c === s.key ? null : c))}
                onDrop={() => drop(s.key)}
                data-testid={`pipeline-col-${s.key}`}
                className={`shrink-0 w-72 rounded-xl border transition-colors ${
                  overCol === s.key ? "border-[#00B4D8] bg-cyan-50/50" : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="flex items-center justify-between px-3.5 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accent }} />
                    <span className="font-display font-bold text-sm text-slate-800">{s.label}</span>
                  </div>
                  <span className="text-xs font-semibold text-slate-400 bg-white border border-slate-200 rounded-full px-2 py-0.5">{items.length}</span>
                </div>
                <div className="p-2.5 space-y-2.5 min-h-[120px] max-h-[calc(100vh-320px)] overflow-y-auto">
                  {items.map((c) => (
                    <div
                      key={c.deal_key}
                      draggable
                      onDragStart={() => setDrag(c)}
                      onDragEnd={() => { setDrag(null); setOverCol(null); }}
                      onClick={() => setEdit(c)}
                      data-testid={`pipeline-card-${c.deal_key}`}
                      className="group bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md hover:border-[#00B4D8]/50 cursor-pointer transition-all"
                      style={{ borderLeft: `3px solid ${accent}` }}
                    >
                      <div className="flex items-start gap-1.5">
                        <DotsSixVertical size={16} className="text-slate-300 group-hover:text-slate-400 mt-0.5 shrink-0 cursor-grab" weight="bold" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-800 truncate">{c.title}</div>
                          <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                            <User size={13} weight="duotone" /><span className="truncate">{c.client_name}</span>
                          </div>
                          {c.total > 0 && (
                            <div className="flex items-center gap-1 text-xs font-semibold text-slate-700 mt-1.5">
                              <CurrencyDollar size={13} weight="duotone" className="text-[#00B4D8]" />{money(c.total)}
                            </div>
                          )}
                          {c.scheduled_date && (
                            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                              <CalendarBlank size={13} weight="duotone" />{c.scheduled_date}{c.scheduled_time ? ` · ${c.scheduled_time}` : ""}
                            </div>
                          )}
                          {c.assigned_crew_ids?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {c.assigned_crew_ids.map((id) => (
                                <span key={id} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{crewName(id)}</span>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {c.quote_number && <Tag>{c.quote_number}</Tag>}
                            {c.job_number && <Tag>{c.job_number}</Tag>}
                            {c.invoice_number && <Tag>{c.invoice_number}</Tag>}
                            {!c.quote_number && !c.job_number && c.number && <Tag>{c.number}</Tag>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-center text-[11px] text-slate-300 py-6">Drop here</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <QuickEdit card={edit} crew={crew} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}

const Tag = ({ children }) => (
  <span className="text-[10px] font-mono font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{children}</span>
);

function QuickEdit({ card, crew, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [crewIds, setCrewIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!card) return;
    setTitle(card.title || "");
    setStatus(card.status || "");
    setDate(card.scheduled_date || "");
    setTime(card.scheduled_time || "");
    setCrewIds(card.assigned_crew_ids || []);
  }, [card]);

  if (!card) return null;
  const kind = card.kind;
  const statusOptions = kind === "job" ? JOB_STATUS : kind === "quote" ? QUOTE_STATUS : kind === "invoice" ? INVOICE_STATUS : [];

  const toggleCrew = (id) => setCrewIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  const detailLink = kind === "job" ? `/jobs/${card.id}` : kind === "quote" ? `/quotes/${card.id}`
    : kind === "invoice" ? `/invoices` : kind === "client" ? `/clients/${card.id}` : null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { kind, id: card.id };
      if (kind === "job") {
        payload.title = title; payload.status = status;
        payload.scheduled_date = date; payload.scheduled_time = time;
        payload.assigned_crew_ids = crewIds;
      } else if (kind === "quote" || kind === "invoice") {
        payload.status = status;
      }
      await api.pipelineQuickEdit(payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!card} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pipeline-quick-edit">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Kanban size={20} weight="duotone" className="text-[#00B4D8]" /> {card.client_name}
          </DialogTitle>
          <DialogDescription>Quickly update this {kind === "client" ? "lead" : kind} without leaving the board.</DialogDescription>
        </DialogHeader>

        {kind === "client" ? (
          <div className="text-sm text-slate-600 space-y-3">
            <p>This is a <b>new lead</b> with no quote yet. Drag the card to <b>Quoted</b> to start a quote, or open the client to add details.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {kind === "job" && (
              <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" data-testid="qe-title" /></div>
            )}
            {statusOptions.length > 0 && (
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1.5" data-testid="qe-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((s) => <SelectItem key={s} value={s}>{labelize(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {kind === "job" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Scheduled date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="qe-date" /></div>
                  <div><Label>Time</Label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1.5" data-testid="qe-time" /></div>
                </div>
                <div>
                  <Label>Assigned crew</Label>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {crew.length === 0 && <span className="text-xs text-slate-400">No crew members yet.</span>}
                    {crew.map((u) => (
                      <button
                        key={u.id} type="button" onClick={() => toggleCrew(u.id)}
                        className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                          crewIds.includes(u.id) ? "bg-[#00B4D8] border-[#00B4D8] text-white" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        {u.name}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {card.total > 0 && (
              <div className="text-sm text-slate-500">Total: <span className="font-semibold text-slate-800">{money(card.total)}</span> <span className="text-xs">(edit line items on the detail page)</span></div>
            )}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {detailLink && (
            <Link to={detailLink} className="text-sm text-[#00B4D8] hover:underline flex items-center gap-1" data-testid="qe-details-link">
              <ArrowSquareOut size={15} weight="bold" /> Full details
            </Link>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {kind !== "client" && (
              <Button onClick={save} disabled={saving} className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="qe-save">
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
